import type { ThreadUpdateEvent } from "@/lib/use-visitor-threads-realtime";

/**
 * A `threadUpdated` frame says the thread's last message was deleted (`lastMessageDeletedId`, no preview).
 * The preview, peek and order must then come from `visitorThreads` again. Refetch when the deleted id is the
 * last message we hold, or when we hold none (the query does not carry ids); a different id means an older
 * message went away and the row is still right.
 */
export function shouldRefetchForDeletedLast(
  e: ThreadUpdateEvent,
  heldLastId: string | null | undefined,
): boolean {
  if (!e.lastMessageDeletedId) return false;
  return !heldLastId || heldLastId === e.lastMessageDeletedId;
}

/** Track each thread's last message id from content frames; forget it once that message is deleted. */
export function noteLastMessageId(held: Record<string, string>, e: ThreadUpdateEvent): void {
  if (e.lastMessageDeletedId && held[e.threadId] === e.lastMessageDeletedId) {
    delete held[e.threadId];
  }
  if (e.lastMessageId) held[e.threadId] = e.lastMessageId;
}

/**
 * The shell's consume step for one `threadUpdated` frame: decides the refetch against the held id (the
 * newest content frame's, else the `visitorThreads` row's `queryLastId`), updates the held ids, and
 * neutralises a deletion of an OLDER message (the row stays as it is). The returned frame goes on to the
 * live fold and the announcement, which never fire for a deletion.
 */
export function consumeDeletedLast(
  held: Record<string, string>,
  frame: ThreadUpdateEvent,
  queryLastId?: string | null,
): { frame: ThreadUpdateEvent; refetch: boolean } {
  const refetch = shouldRefetchForDeletedLast(frame, held[frame.threadId] ?? queryLastId);
  noteLastMessageId(held, frame);
  return {
    frame:
      frame.lastMessageDeletedId && !refetch ? { ...frame, lastMessageDeletedId: null } : frame,
    refetch,
  };
}
